/*
Copyright (C) 2020  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import * as React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import {
  ArrowRightLeft,
  CircleAlert,
  Gauge,
  Info,
  LoaderCircle,
  Rocket,
  Settings2,
} from "lucide-react";

import fieldHelper from "@src/@types/Field";
import {
  DeploymentItem,
  TransferItem,
  ActionItem,
  getTransferItemTitle,
} from "@src/@types/MainItem";
import { MinionPool } from "@src/@types/MinionPool";
import EndpointLogos from "@src/components/modules/EndpointModule/EndpointLogos";
import { providerLabel } from "@src/components/modules/EndpointModule/EndpointLogos/ProviderMark";
import CopyMultilineValue from "@src/components/ui/CopyMultilineValue";
import CopyValue from "@src/components/ui/CopyValue";
import {
  CardList,
  CardListRow,
  DetailsColumns,
  KeyValueGrid,
  SectionCard,
} from "@src/components/ui/DetailsKit";
import type { KvItem } from "@src/components/ui/DetailsKit/DetailsKit";
import IconTile from "@src/components/ui/IconTile";
import {
  Pill,
  StatePill,
  T9,
  formatDateTime,
  timeAgo,
} from "@src/components/ui/Lists/ListTable";
import { OptionsSchemaPlugin } from "@src/plugins";
import LabelDictionary from "@src/utils/LabelDictionary";
import { isMachineAllocated } from "./MinionPoolMachines";

import type { Endpoint } from "@src/@types/Endpoint";
import type { Field as FieldType } from "@src/@types/Field";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;
const ValueLink = styled(Link)`
  color: ${T9.brand600};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;
const RowLink = styled(Link)`
  color: ${T9.slate900};
  font-weight: 500;
  text-decoration: none;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  &:hover {
    color: ${T9.brand600};
    text-decoration: underline;
  }
`;
const Missing = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--t9-danger-700);
`;
const Spinner = styled(LoaderCircle)`
  color: ${T9.slate400};
  animation: mpm-spin 1s linear infinite;
  @keyframes mpm-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const Empty = styled.div`
  font-size: 14px;
  color: ${T9.slate500};
  padding: 4px 0 8px 0;
`;
const Scroll = styled.div`
  max-height: 360px;
  overflow: auto;
`;
const Muted = styled.span`
  color: ${T9.slate500};
`;

/** The option labels come from the upstream dictionary ("Minion Image"...): Swift calls them agents. */
export const agentLabel = (label: string) =>
  String(label)
    .replace(/\bMinions\b/g, "Agents")
    .replace(/\bMinion\b/g, "Agent")
    .replace(/\bminions\b/g, "agents")
    .replace(/\bminion\b/g, "agent");

const formatIdleTime = (seconds?: number | null) => {
  if (!seconds) {
    return null;
  }
  if (seconds < 60) {
    return `${seconds} s`;
  }
  const minutes = Math.round(seconds / 60);
  const human =
    minutes < 60
      ? `${minutes} min`
      : `${Math.floor(minutes / 60)} h${minutes % 60 ? ` ${minutes % 60} min` : ""}`;
  return (
    <span>
      {human} <Muted>({seconds} s)</Muted>
    </span>
  );
};

type Props = {
  item?: MinionPool | null;
  transfers: TransferItem[];
  deployments: DeploymentItem[];
  schema: FieldType[];
  schemaLoading: boolean;
  endpoints: Endpoint[];
  bottomControls: React.ReactNode;
};
@observer
class MinionPoolMainDetails extends React.Component<Props> {
  getEndpoint(): Endpoint | undefined {
    const endpoint = this.props.endpoints.find(
      e => e.id === this.props.item?.endpoint_id,
    );
    return endpoint;
  }

  renderValue(value: string, capitalize?: boolean) {
    return <CopyValue value={value} maxWidth="100%" capitalize={capitalize} />;
  }

  renderEndpointLink(): React.ReactNode {
    const endpoint = this.getEndpoint();

    if (endpoint) {
      return (
        <ValueLink to={`/endpoints/${endpoint.id}`}>{endpoint.name}</ValueLink>
      );
    }

    return (
      <Missing>
        <CircleAlert size={14} />
        Endpoint is missing
      </Missing>
    );
  }

  getPropertyNames() {
    const env = this.props.item?.environment_options;
    return env
      ? Object.keys(env).filter(
          k =>
            k !== "network_map" &&
            (k !== "storage_mappings" ||
              (env[k] != null &&
                typeof env[k] === "object" &&
                Object.keys(env[k]).length > 0)),
        )
      : [];
  }

  renderPropertiesTable(propertyNames: string[]) {
    const endpoint = this.getEndpoint();

    const getValue = (name: string, value: any) => {
      if (
        value.join &&
        value.length &&
        value[0].destination &&
        value[0].source
      ) {
        return value
          .map(
            (v: { source: any; destination: any }) =>
              `${v.source}=${v.destination}`,
          )
          .join(", ");
      }
      const schema = this.props.schema;
      return fieldHelper.getValueAlias({
        name,
        value,
        fields: schema,
        targetProvider: endpoint && endpoint.type,
      });
    };

    let properties: any[] = [];
    const plugin = endpoint && OptionsSchemaPlugin.for(endpoint.type);
    const deploymentImageMapFieldName =
      plugin && plugin.migrationImageMapFieldName;
    let dictionaryKey = "";
    if (endpoint) {
      dictionaryKey = `${endpoint.type}-minion-pool`;
    }
    const environment = this.props.item?.environment_options;
    propertyNames.forEach(pn => {
      const value = environment ? environment[pn] : "";
      const label = LabelDictionary.get(pn, dictionaryKey);

      if (value && value.join) {
        value.forEach((v: any, i: number) => {
          const useLabel = i === 0 ? label : "";
          properties.push({ label: useLabel, value: v });
        });
      } else if (value && typeof value === "object") {
        properties = properties.concat(
          Object.keys(value).map(p => {
            if (p === "disk_mappings") {
              return null;
            }
            let fieldName = pn;
            if (
              deploymentImageMapFieldName &&
              fieldName === deploymentImageMapFieldName
            ) {
              fieldName = p;
            }
            return {
              label: `${label} - ${LabelDictionary.get(p)}`,
              value: getValue(fieldName, value[p]),
            };
          }),
        );
      } else {
        properties.push({ label, value: getValue(pn, value) });
      }
    });

    const items: KvItem[] = properties
      .filter(Boolean)
      .filter(p => p.value != null && p.value !== "")
      .map((prop, i, all) => ({
        // A list option repeats its label on each value, so each pair still says what it is.
        label: agentLabel(prop.label || all[i - 1]?.label || ""),
        value: <CopyValue value={String(prop.value)} maxWidth="100%" />,
      }));
    return <KeyValueGrid items={items} columns={2} />;
  }

  renderUsage(items: ActionItem[]) {
    if (!items.length) {
      return <Empty>No transfer or deployment uses this SwiftIQ Agent yet.</Empty>;
    }
    return (
      <Scroll>
        <CardList>
          {items.map(item => {
            const isTransfer = item.type === "transfer";
            const actionHref = isTransfer ? "transfers" : "deployments";
            const title = getTransferItemTitle(item) || item.instances[0] || item.id;
            return (
              <CardListRow key={item.id}>
                <IconTile
                  icon={isTransfer ? ArrowRightLeft : Rocket}
                  size={24}
                  tone={isTransfer ? "info" : "brand"}
                />
                <RowLink to={`/${actionHref}/${item.id}`} title={title}>
                  {title}
                </RowLink>
                <Pill tone="neutral">{isTransfer ? "Transfer" : "Deployment"}</Pill>
                <StatePill status={item.last_execution_status} />
              </CardListRow>
            );
          })}
        </CardList>
      </Scroll>
    );
  }

  renderOverview() {
    const item = this.props.item;
    const endpoint = this.getEndpoint();
    return (
      <SectionCard
        title="Overview"
        icon={Info}
        actions={endpoint ? <EndpointLogos endpoint={endpoint.type as any} height={42} /> : null}
        testId="minionPoolDetails-overview"
      >
        <KeyValueGrid
          columns={4}
          items={[
            { label: "Endpoint", value: this.renderEndpointLink() },
            { label: "Provider", value: endpoint ? providerLabel(endpoint.type) : null },
            {
              label: "Platform",
              value: item?.platform
                ? item.platform === "source"
                  ? "Source agents"
                  : "Target agents"
                : null,
            },
            {
              label: "OS type",
              value: item?.os_type ? (item.os_type === "windows" ? "Windows" : "Linux") : null,
            },
            { label: "Status", value: item?.status ? <StatePill status={item.status} /> : null },
            {
              label: "Created",
              value: item?.created_at ? formatDateTime(item.created_at) : null,
            },
            {
              label: "Last updated",
              span: 2,
              value: item?.updated_at ? (
                <span title={formatDateTime(item.updated_at)}>
                  {formatDateTime(item.updated_at)} · {timeAgo(item.updated_at)}
                </span>
              ) : null,
            },
            {
              label: "ID",
              mono: true,
              span: 2,
              value: item?.id ? this.renderValue(item.id) : null,
            },
            {
              label: "Notes",
              span: 4,
              hidden: !item?.notes,
              value: item?.notes ? <CopyMultilineValue value={item.notes} /> : null,
            },
          ]}
        />
      </SectionCard>
    );
  }

  renderScaling() {
    const item = this.props.item;
    const machines = item?.minion_machines || [];
    const allocated = machines.filter(isMachineAllocated).length;
    return (
      <SectionCard title="Scaling" icon={Gauge} testId="minionPoolDetails-scaling">
        <KeyValueGrid
          columns={2}
          items={[
            { label: "Minimum agents", value: String(item?.minimum_minions || "1") },
            { label: "Maximum agents", value: String(item?.maximum_minions || "1") },
            {
              label: "Agent machines",
              value: `${machines.length} (${allocated} allocated)`,
            },
            {
              label: "Retention strategy",
              value:
                (item?.minion_retention_strategy || "delete") === "poweroff"
                  ? "Power off"
                  : "Delete",
            },
            { label: "Maximum idle time", value: formatIdleTime(item?.minion_max_idle_time) },
          ]}
        />
      </SectionCard>
    );
  }

  renderEnvironment() {
    const propertyNames = this.getPropertyNames();
    return (
      <SectionCard
        title={
          <>
            Environment options
            {this.props.schemaLoading ? <Spinner size={14} /> : null}
          </>
        }
        icon={Settings2}
        testId="minionPoolDetails-environment"
      >
        {propertyNames.length > 0 ? (
          this.renderPropertiesTable(propertyNames)
        ) : (
          <Empty>No environment options are set.</Empty>
        )}
      </SectionCard>
    );
  }

  render() {
    const usage: ActionItem[] = this.props.transfers.concat(
      this.props.deployments as any[],
    );

    return (
      <Wrapper>
        {this.renderOverview()}
        <DetailsColumns>
          {this.renderEnvironment()}
          {this.renderScaling()}
        </DetailsColumns>
        <SectionCard
          title={`Used by transfers (${usage.length})`}
          icon={ArrowRightLeft}
          testId="minionPoolDetails-usage"
        >
          {this.renderUsage(usage)}
        </SectionCard>
        {this.props.bottomControls}
      </Wrapper>
    );
  }
}

export default MinionPoolMainDetails;
